import { useState, useEffect } from 'react';
import { FaEnvelope, FaUsers, FaPaperPlane, FaHistory, FaSpinner, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function NewsletterManagement() {
  const [activeTab, setActiveTab] = useState('compose');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Fetch subscribers count
  const { data: subscribersData } = useSWR('/api/newsletter/subscribers?active=true&limit=1', Fetcher);
  const subscribersCount = subscribersData?.data?.pagination?.total || 0;

  // Fetch campaigns
  const { data: campaignsData, mutate: mutateCampaigns } = useSWR(
    activeTab === 'history' ? '/api/newsletter/campaigns' : null,
    Fetcher
  );

  const handleSendNewsletter = async () => {
    if (!subject.trim()) {
      toast.error('Subject is required');
      return;
    }

    if (!content.trim()) {
      toast.error('Content is required');
      return;
    }

    if (subscribersCount === 0) {
      toast.error('No active subscribers found');
      return;
    }

    setIsSending(true);
    try {
      const payload = {
        subject: subject.trim(),
        content: content.trim(),
        ...(scheduledFor && { scheduledFor: new Date(scheduledFor).toISOString() }),
      };

      const response = await HttpInterceptor.post('/api/newsletter/send', payload);
      
      toast.success(response.data.message || 'Newsletter sent successfully!');
      
      // Clear form
      setSubject('');
      setContent('');
      setScheduledFor('');
      
      // Refresh campaigns if on history tab
      if (activeTab === 'history') {
        mutateCampaigns();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send newsletter');
    } finally {
      setIsSending(false);
    }
  };

  const campaigns = campaignsData?.data?.campaigns || [];

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Newsletter Management</h1>
          <p className="text-gray-600">Compose and send newsletters to your subscribers</p>
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <FaUsers className="text-blue-600 text-2xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Subscribers</p>
              <p className="text-3xl font-bold text-gray-900">{subscribersCount}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('compose')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'compose'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaPaperPlane className="text-lg" />
                Compose Newsletter
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaHistory className="text-lg" />
                Campaign History
              </button>
            </nav>
          </div>

          {/* Compose Tab */}
          {activeTab === 'compose' && (
            <div className="p-6 space-y-6">
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Line
                </label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter newsletter subject"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  Newsletter Content
                </label>
                <textarea
                  id="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={15}
                  placeholder="Write your newsletter content here. You can use plain text or HTML formatting."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Tip: Use line breaks for paragraphs. HTML tags are supported for formatting.
                </p>
              </div>

              <div>
                <label htmlFor="scheduledFor" className="block text-sm font-medium text-gray-700 mb-2">
                  Schedule (Optional)
                </label>
                <input
                  type="datetime-local"
                  id="scheduledFor"
                  value={scheduledFor}
                  onChange={(e) => setScheduledFor(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Leave empty to send immediately. Set a future date/time to schedule the newsletter.
                </p>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <button
                  onClick={handleSendNewsletter}
                  disabled={isSending || !subject.trim() || !content.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      {scheduledFor ? 'Schedule Newsletter' : 'Send Newsletter'}
                    </>
                  )}
                </button>
              </div>

              {subscribersCount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This newsletter will be sent to <strong>{subscribersCount}</strong> active subscriber{subscribersCount !== 1 ? 's' : ''}.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="p-6">
              {campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <FaHistory className="text-4xl text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No newsletter campaigns yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map((campaign) => (
                    <div
                      key={campaign._id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{campaign.subject}</h3>
                          <p className="text-sm text-gray-600">
                            Sent by: {campaign.sentBy} • {new Date(campaign.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {campaign.status === 'sent' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                              <FaCheckCircle />
                              Sent
                            </span>
                          )}
                          {campaign.status === 'sending' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              <FaSpinner className="animate-spin" />
                              Sending
                            </span>
                          )}
                          {campaign.status === 'failed' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                              <FaTimesCircle />
                              Failed
                            </span>
                          )}
                          {campaign.status === 'scheduled' && (
                            <span className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                              Scheduled
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Recipients:</span>
                          <span className="ml-2 font-semibold">{campaign.recipientsCount}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Sent:</span>
                          <span className="ml-2 font-semibold text-green-600">{campaign.sentCount}</span>
                        </div>
                        {campaign.failedCount > 0 && (
                          <div>
                            <span className="text-gray-600">Failed:</span>
                            <span className="ml-2 font-semibold text-red-600">{campaign.failedCount}</span>
                          </div>
                        )}
                      </div>
                      {campaign.scheduledFor && (
                        <p className="text-xs text-gray-500 mt-2">
                          Scheduled for: {new Date(campaign.scheduledFor).toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NewsletterManagement;

